import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { Email, Phone, Business } from "@mui/icons-material";
import {
  validateBusinessToken,
  submitFeedback,
} from "../../services/feedbackService";
import ProgressBar from "../../components/ProgressBar";
import MoodCheck from "../Feedback/questions/MoodCheck";
import EnergyLevel from "../Feedback/questions/EnergyLevel";
import WellnessPoll from "../Feedback/questions/WellnessPoll";
import WorkEnvironmentFeedback from "../Feedback/questions/WorkEnvironmentFeedback";
import Recognition from "../Feedback/questions/Recognition";
import ExpressYourself from "../Feedback/questions/ExpressYourself";
import WorkloadSatisfaction from "../Feedback/questions/WorkloadSatisfaction";
import Engagement from "../Feedback/questions/Engagement";
import TeamDynamicsInsight from "../Feedback/questions/TeamDynamicsInsight";
import { useMessage } from "../../contexts/MessageContext";
import backgroundImage from "../../assets/background.webp";

// Utility function to shuffle an array
const shuffleArray = (array) => {
  return [...array].sort(() => Math.random() - 0.5);
};

const Feedback = () => {
  const { businessToken } = useParams();
  const { showMessage } = useMessage();
  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [businessDetails, setBusinessDetails] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [feedbackId, setFeedbackId] = useState(null);

  // Validate business token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!businessToken) {
        setErrorMessage("No token provided. Please use a valid feedback link.");
        setLoading(false);
        return;
      }

      try {
        const data = await validateBusinessToken(businessToken);
        setBusinessDetails(data);
        setValidToken(true);
      } catch (error) {
        setErrorMessage("Invalid Feedback Link!");
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [businessToken]);

  // Function to shuffle and start session
  const startSession = () => {
    if (!businessDetails) {
      showMessage("Business details missing. Please reload.", "error");
      return;
    }

    const questions = [
      { component: <MoodCheck />, key: "moodCheck" },
      { component: <EnergyLevel />, key: "energyLevel" },
      { component: <WellnessPoll />, key: "wellnessPoll" },
      { component: <WorkEnvironmentFeedback />, key: "workEnvironment" },
      {
        component: (
          <Recognition
            businessId={businessDetails.business_id}
            onResponseChange={handleResponseChange}
          />
        ),
        key: "recognition",
      },
      {
        component: <ExpressYourself onResponseChange={handleResponseChange} />,
        key: "expressYourself",
      },
      { component: <WorkloadSatisfaction />, key: "workloadSatisfaction" },
      { component: <Engagement />, key: "engagement" },
      { component: <TeamDynamicsInsight />, key: "teamDynamics" },
    ];

    setShuffledQuestions(shuffleArray(questions));
    setCurrentStep(1);
    setCurrentQuestionIndex(0);
    setResponses({});
    setFeedbackId(null);
  };

  // Handle response changes
  const handleResponseChange = (questionKey, response) => {
    setResponses((prev) => {
      const updatedResponses = { ...prev, [questionKey]: response };
      return updatedResponses;
    });
  };

  // Move to the next question
  const handleNextClick = async () => {
    if (!businessDetails) {
      showMessage("Invalid session. Please reload the page.", "error");
      return;
    }

    try {
      const response = await submitFeedback({
        business_token: businessToken,
        feedback_id: feedbackId,
        question_key: shuffledQuestions[currentQuestionIndex].key,
        response:
          responses[shuffledQuestions[currentQuestionIndex].key] || null,
      });

      // If it's the first request, store the feedback ID
      if (!feedbackId) {
        setFeedbackId(response.id);
      }

      setCurrentQuestionIndex((prev) => prev + 1);
    } catch (error) {
      showMessage("Failed to save response.", "error");
    }
  };

  // Submit feedback
  const handleSubmitFeedback = async () => {
    if (!feedbackId) {
      showMessage("Session ID missing! Cannot submit.", "error");
      return;
    }

    try {
      console.log("Submitting final feedback:", responses); // Debugging

      await submitFeedback({
        business_token: businessToken,
        feedback_id: feedbackId,
        question_key: shuffledQuestions[currentQuestionIndex].key,
        response:
          responses[shuffledQuestions[currentQuestionIndex].key] || null,
        is_complete: true,
      });

      showMessage("Feedback submitted successfully!", "success");
      handleRestart();
    } catch (error) {
      showMessage("Failed to submit feedback. Please try again.", "error");
    }
  };

  const handleRestart = () => {
    setFeedbackId(null);
    setCurrentStep(0);
    setCurrentQuestionIndex(0);
    setResponses({});
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!validToken) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100vh",
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          padding: "2rem",
          color: "white",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
        }}
      >
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            borderRadius: "16px",
            padding: "2rem",
            maxWidth: "450px",
            textAlign: "center",
            boxShadow: "0px 12px 30px rgba(0, 0, 0, 0.3)",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "error.main",
              marginBottom: "1rem",
            }}
          >
            {errorMessage || "Invalid Token"}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1.25rem",
              lineHeight: 1.6,
              color: "text.secondary",
              marginBottom: "2rem",
            }}
          >
            The token provided is invalid or missing. Please ensure you have a
            valid feedback link. If the issue persists, contact your
            administrator.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f4f4f4" }}>
      {currentStep === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: "2rem",
            height: "100vh",
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            color: "white",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
          }}
        >
          {/* Business Details */}
          {businessDetails && (
            <Box
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                padding: "2rem",
                minWidth: { sm: "400px" },
                maxWidth: { sm: "600px" },
                textAlign: "center",
                boxShadow: "0px 12px 30px rgba(0, 0, 0, 0.3)",
                margin: "0 auto",
                marginBottom: "2rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginBottom: "1.5rem",
                }}
              >
                <Business sx={{ fontSize: "3rem", color: "primary.main" }} />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: "bold",
                    color: "primary.main",
                    textTransform: "uppercase",
                    letterSpacing: "0.1rem",
                  }}
                >
                  {businessDetails.business_name}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginBottom: "0.5rem",
                }}
              >
                <Email sx={{ fontSize: "1.5rem", color: "text.secondary" }} />
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", fontSize: "1rem" }}
                >
                  {businessDetails.business_email}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <Phone sx={{ fontSize: "1.5rem", color: "text.secondary" }} />
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", fontSize: "1rem" }}
                >
                  {businessDetails.business_phone}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Welcome Text */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              marginBottom: "1rem",
              textTransform: "uppercase",
              color: "white",
            }}
          >
            Welcome to the Engagement Session
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1.25rem",
              lineHeight: 1.6,
              color: "#e6e6e6",
              maxWidth: "600px",
              marginBottom: "2rem",
            }}
          >
            This session is completely anonymous. Your honest input will help us
            improve the workplace environment for everyone.
          </Typography>

          {/* Start Button */}
          <Button
            variant="contained"
            size="large"
            onClick={startSession}
            sx={{
              letterSpacing: "0.2rem",
              backgroundColor: "accent.main",
              color: "primary.main",
              borderRadius: "50px",
              padding: "0.25rem 3rem",
              fontSize: "1.75rem",
            }}
          >
            Start
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <ProgressBar
            current={currentQuestionIndex + 1}
            total={shuffledQuestions.length}
          />
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              padding: "2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Restart Button - Fixed to the top-left corner */}
            <Button
              onClick={handleRestart}
              sx={{
                position: "fixed",
                top: { xs: "100px", sm: "70px" },
                left: "10px",
                letterSpacing: "0.2rem",
                backgroundColor: "accent.main",
                color: "primary.main",
                borderRadius: "50px",
                padding: "0.5rem 1.5rem",
                fontSize: "1.15rem",
                zIndex: 1000,
              }}
            >
              Restart
            </Button>
            {shuffledQuestions.length > 0 &&
              React.cloneElement(
                shuffledQuestions[currentQuestionIndex].component,
                { onResponseChange: handleResponseChange }
              )}
          </Box>
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              height: "20vh",
              backgroundColor: "white",
              borderTop: "1px solid #ddd",
              padding: "1rem 2rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Button
              variant="outlined"
              disabled={currentQuestionIndex === 0}
              onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
              sx={{
                letterSpacing: "0.2rem",
                borderRadius: "50px",
                padding: "0.5rem 1.5rem",
                fontSize: "1.15rem",
              }}
            >
              Previous
            </Button>
            {currentQuestionIndex < shuffledQuestions.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNextClick}
                sx={{
                  letterSpacing: "0.2rem",
                  borderRadius: "50px",
                  padding: "0.5rem 1.5rem",
                  fontSize: "1.15rem",
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmitFeedback}
                sx={{
                  letterSpacing: "0.2rem",
                  borderRadius: "50px",
                  padding: "0.5rem 1.5rem",
                  fontSize: "1.15rem",
                }}
              >
                Submit
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Feedback;
